import { useCallback, useMemo } from "react";

import {
  faChartMixed,
  faEdit,
  faFlask,
  faRefresh,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { noop } from "lodash-es";
import { AnimatePresence, m } from "motion/react";
import { useLocalStorage } from "usehooks-ts";

import { useChartExplore } from "src/analytics/copilot/useChartExplore";
import { DashboardEditActions } from "src/analytics/dashboard/DashboardEditActions";
import { DashboardFormModal } from "src/analytics/dashboard/DashboardFormModal";
import { DashboardGrid } from "src/analytics/dashboard/DashboardGrid";
import {
  DashboardMetricsProvider,
  dashboardMetricsKeys,
} from "src/analytics/dashboard/DashboardMetricsContext";
import { DashboardTabs } from "src/analytics/dashboard/DashboardTabs";
import { defaultDashboardWindow } from "src/analytics/dashboard/dashboardFilterParams";
import { JustPinnedProvider } from "src/analytics/dashboard/JustPinnedContext";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import { useDashboardLayoutEditing } from "src/analytics/dashboard/useDashboardLayoutEditing";
import { metricsQueryKeys } from "src/analytics/metrics/useMetricsQuery";
import {
  dashboardKeys,
  useDeleteDashboard,
  useListDashboardsQuery,
} from "src/api/analytics-dashboard/dashboard";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { CaseCopilotSidebar } from "src/cases/analytics/CaseCopilotSidebar";
import { CASE_TAKTILE_CHART_CONFIG } from "src/cases/analytics/caseAnalyticsChartConfig";
import { useCaseAnalyticsLabels } from "src/cases/analytics/useCaseAnalyticsLabels";
import { CasePageLayout } from "src/cases/CasePageLayout";
import { ChartConfigProvider } from "src/chartSandbox/SandboxedChart";
import { Realm } from "src/clients/analytics-dashboard";
import { CopilotButton } from "src/design-system/CopilotButton/CopilotButton";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useEnvironment } from "src/hooks/useEnvironment";
import { SubHeaderButton, SubHeaderDatePicker } from "src/layout/SubHeader";
import {
  DashboardPageParams,
  getUrlToCaseAnalyticsPage,
} from "src/router/urls";
import { type StrictDateRange, useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

const DATE_FILTER_TOOLTIP =
  "Date filter applies to each Case's last activity in the range. For charts tracking new Cases, it applies to the creation date.";

export const CaseAnalyticsContent: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const [environment, setEnvironment] = useEnvironment();
  const labelDictionaries = useCaseAnalyticsLabels();
  const [isCopilotOpen, setIsCopilotOpen] = useLocalStorage(
    "caseAnalyticsCopilotOpen",
    false,
  );
  const openCopilot = useCallback(
    () => setIsCopilotOpen(true),
    [setIsCopilotOpen],
  );
  const { pendingChartContext, exploreWithCopilot, clearPendingContext } =
    useChartExplore(openCopilot);
  const defaultWindow = useMemo<StrictDateRange>(
    () => defaultDashboardWindow(),
    [],
  );
  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow<StrictDateRange>(defaultWindow, "caseAnalyticsTimeWindow");

  const queryClient = useQueryClient();
  const isRefreshingDashboards =
    useIsFetching({ queryKey: dashboardKeys.all(wsId) }) > 0;
  const isRefreshingPinnedCharts =
    useIsFetching({ queryKey: dashboardMetricsKeys.all }) > 0;
  const isRefreshingChatCharts =
    useIsFetching({ queryKey: metricsQueryKeys.all }) > 0;
  const isRefreshing =
    isRefreshingDashboards ||
    isRefreshingPinnedCharts ||
    isRefreshingChatCharts;
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all(wsId) });
    queryClient.invalidateQueries({ queryKey: dashboardMetricsKeys.all });
    queryClient.invalidateQueries({ queryKey: metricsQueryKeys.all });
  };

  const { data: dashboards, isLoading: isLoadingDashboards } =
    useListDashboardsQuery(Realm.CASE_ANALYTICS, wsId);

  const [activeDashboardId, setActiveDashboardId] = useLocalStorage<
    string | undefined
  >(`caseAnalyticsActiveDashboard:${wsId}`, undefined);

  const resolvedDashboardId =
    activeDashboardId && dashboards?.some((d) => d.id === activeDashboardId)
      ? activeDashboardId
      : dashboards?.[0]?.id;
  const hasDashboards = (dashboards?.length ?? 0) > 0;

  const { dashboard } = useDashboard(
    Realm.CASE_ANALYTICS,
    wsId,
    resolvedDashboardId,
  );
  const pins = Object.values(dashboard.pins);

  const editing = useDashboardLayoutEditing(
    Realm.CASE_ANALYTICS,
    wsId,
    resolvedDashboardId,
  );

  const { caseAnalyticsDashboards: dashboardPermissions } = useCapabilities();
  const deleteDashboard = useDeleteDashboard(Realm.CASE_ANALYTICS, wsId);

  const {
    isOpen: isFormOpen,
    data: editingDashboardId,
    openModal: openFormModal,
    closeModal: closeFormModal,
    afterLeave: formAfterLeave,
  } = useModal<string>();

  const {
    isOpen: isDeleteOpen,
    data: deletingDashboardId,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
    afterLeave: deleteAfterLeave,
  } = useModal<string>();

  const deletingDashboardName =
    dashboards?.find((d) => d.id === deletingDashboardId)?.name ??
    "this dashboard";

  const handleDeleteConfirm = async () => {
    if (!deletingDashboardId) return;
    try {
      await deleteDashboard.mutateAsync(deletingDashboardId);
      if (activeDashboardId === deletingDashboardId) {
        setActiveDashboardId(undefined);
      }
      toastActions.success({ title: "Successfully deleted dashboard" });
    } catch {
      toastActions.failure({ title: "Error deleting dashboard" });
    }
    closeDeleteModal();
  };

  // Keyed by workspace, not dashboard — see JustPinnedContext.spec for why.
  return (
    <ChartConfigProvider value={CASE_TAKTILE_CHART_CONFIG}>
      <JustPinnedProvider key={`${Realm.CASE_ANALYTICS}:${wsId}`}>
        <DashboardMetricsProvider
          dataSource="case_analytics"
          pins={pins}
          timeWindow={timeWindow}
        >
          <CasePageLayout
            breadcrumbLabel="Analytics"
            breadcrumbHref={getUrlToCaseAnalyticsPage(orgId, wsId)}
            headerActions={
              <AnimatePresence initial={false}>
                {!isCopilotOpen && !editing.isEditing && (
                  <m.div
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0.95 }}
                    key="ask-copilot"
                    className="px-3.5"
                    transition={{ duration: 0.15 }}
                  >
                    <CopilotButton
                      dataLoc="case-analytics-ask-copilot-button"
                      onClick={() => setIsCopilotOpen(true)}
                    >
                      Ask Copilot
                    </CopilotButton>
                  </m.div>
                )}
              </AnimatePresence>
            }
            rightPanel={
              <AnimatePresence initial={false} mode="wait">
                {isCopilotOpen && !editing.isEditing && (
                  <CaseCopilotSidebar
                    key="case-copilot-sidebar"
                    dashboardId={resolvedDashboardId}
                    env={environment}
                    isLayoutEditing={editing.isEditing}
                    isLoadingDashboards={isLoadingDashboards}
                    labelDictionaries={labelDictionaries}
                    onClearPendingStructuredInput={clearPendingContext}
                    onClose={() => setIsCopilotOpen(false)}
                    onDashboardAutoCreated={setActiveDashboardId}
                    pendingStructuredInput={pendingChartContext}
                    wsId={wsId}
                  />
                )}
              </AnimatePresence>
            }
            removePadding
          >
            {hasDashboards && resolvedDashboardId && (
              <div
                className="flex h-full flex-col"
                data-loc="case-analytics-content"
              >
                <DashboardTabs
                  activeDashboardId={resolvedDashboardId}
                  canCreate={dashboardPermissions.canCreate}
                  realm={Realm.CASE_ANALYTICS}
                  realmId={wsId}
                  onCreateRequest={() => openFormModal(undefined)}
                  onDashboardChange={setActiveDashboardId}
                  onDeleteRequest={openDeleteModal}
                  onEditRequest={openFormModal}
                >
                  {editing.isEditing ? (
                    <DashboardEditActions
                      isSaving={editing.isSaving}
                      onCancel={editing.cancel}
                      onSave={editing.save}
                    />
                  ) : (
                    <>
                      <SubHeaderDatePicker
                        infoTooltip={DATE_FILTER_TOOLTIP}
                        placeholder="Last 30 days"
                        value={dateRangePickerValue}
                        onChange={onDateRangePickerChange}
                      />
                      <SubHeaderButton
                        icon={faRefresh}
                        spin={isRefreshing}
                        tooltip="Refresh"
                        onClick={isRefreshing ? noop : handleRefresh}
                      />
                      <span className="h-full w-12 border-gray-200 border-l p-3.5">
                        <EllipsisOptionsDropdown
                          dataLoc="case-analytics-page-menu"
                          elements={[
                            {
                              key: "Organize dashboard",
                              icon: faEdit,
                              action: editing.start,
                              disabled: !editing.canEdit,
                            },
                            {
                              key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"} environment`,
                              icon:
                                environment === "sandbox"
                                  ? faWavePulse
                                  : faFlask,
                              action: () =>
                                setEnvironment(
                                  environment === "sandbox"
                                    ? "live"
                                    : "sandbox",
                                ),
                            },
                          ]}
                          iconSize="sm"
                          placement="bottom start"
                        />
                      </span>
                    </>
                  )}
                </DashboardTabs>
                <div className="flex h-full flex-col overflow-auto p-5">
                  <DashboardGrid
                    dashboardId={resolvedDashboardId}
                    editLayout={editing.layout}
                    isEditing={editing.isEditing}
                    isSaving={editing.isSaving}
                    labelDictionaries={labelDictionaries}
                    onEditLayoutChange={editing.onLayoutChange}
                    onExploreWithCopilot={exploreWithCopilot}
                    onOpenCopilot={openCopilot}
                    realm={Realm.CASE_ANALYTICS}
                    realmId={wsId}
                  />
                </div>
              </div>
            )}
            {isLoadingDashboards && (
              <div
                className="grid grid-cols-3 gap-4 p-5"
                data-loc="case-analytics-loading-dashboards"
              >
                <Skeleton height="h-48" />
                <Skeleton height="h-48" />
                <Skeleton height="h-48" />
              </div>
            )}
            {!isLoadingDashboards && !hasDashboards && (
              <div
                className="flex h-full flex-col"
                data-loc="case-analytics-content"
              >
                <EmptyState
                  actionButton={
                    dashboardPermissions.canCreate
                      ? {
                          label: "Create dashboard",
                          onClick: () => openFormModal(undefined),
                        }
                      : undefined
                  }
                  dataLoc="case-analytics-empty-state"
                  description="Create a dashboard or pin a chart from Copilot to get started."
                  headline="No dashboards yet"
                  icon={faChartMixed}
                />
              </div>
            )}
            <DashboardFormModal
              afterLeave={formAfterLeave}
              dashboardId={editingDashboardId}
              open={isFormOpen}
              realm={Realm.CASE_ANALYTICS}
              realmId={wsId}
              onClose={closeFormModal}
              onCreated={setActiveDashboardId}
            />
            <ConfirmationModal
              afterLeave={deleteAfterLeave}
              confirmationButtonText="Delete"
              dataLoc="delete-dashboard-modal"
              open={isDeleteOpen}
              title="Delete this dashboard?"
              variant="danger"
              onClose={closeDeleteModal}
              onConfirm={handleDeleteConfirm}
              description={`Are you sure you want to delete "${deletingDashboardName}"? This action cannot be undone.`}
            />
          </CasePageLayout>
        </DashboardMetricsProvider>
      </JustPinnedProvider>
    </ChartConfigProvider>
  );
};
